import { useEffect, useState } from "react";
import { autoReloadIfAllowed, reconnectFirestore } from "../lib/firestore-recovery";
import { diag } from "../lib/diag";

const RECONNECT_AFTER_MS = 6_000;
const SHOW_NOTICE_AFTER_MS = 10_000;
const AUTO_RELOAD_AFTER_MS = 25_000;

/**
 * Runs `start()` now and again every time the app comes back to the
 * foreground, calling the cleanup it returns when the app is hidden. Timers
 * therefore only count time the app is actually on screen: iOS pauses them in
 * the background and fires them all at once on return, which made a screen
 * that had just been reopened look "stuck for minutes".
 */
export function whileVisible(start: () => () => void): () => void {
  let stop: (() => void) | null = document.visibilityState === "visible" ? start() : null;
  const onChange = () => {
    if (document.visibilityState === "visible") {
      if (!stop) stop = start();
    } else if (stop) {
      stop();
      stop = null;
    }
  };
  document.addEventListener("visibilitychange", onChange);
  return () => {
    document.removeEventListener("visibilitychange", onChange);
    stop?.();
  };
}

/**
 * Watches a screen's loading state so it can never spin forever:
 * after 6s it re-opens Firestore's connection (enough for a stalled network),
 * after 10s it returns true so the screen shows a "try again" button (which
 * restarts the app), and after 25s it restarts the app on its own (limited,
 * see autoReloadIfAllowed). Only time on screen counts. `screen` names the
 * screen in the diagnostics.
 */
export function useSlowLoading(loading: boolean, screen: string, attempt = 0): boolean {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    setSlow(false);
    if (!loading) return;
    return whileVisible(() => {
      const started = Date.now();
      const reconnect = setTimeout(() => {
        diag("slow load", { screen, ms: Date.now() - started });
        void reconnectFirestore(`slow load: ${screen}`);
      }, RECONNECT_AFTER_MS);
      const notice = setTimeout(() => setSlow(true), SHOW_NOTICE_AFTER_MS);
      const reload = setTimeout(() => {
        diag("still loading", { screen, ms: Date.now() - started });
        autoReloadIfAllowed(`${screen} stuck loading`);
      }, AUTO_RELOAD_AFTER_MS);
      return () => {
        clearTimeout(reconnect);
        clearTimeout(notice);
        clearTimeout(reload);
      };
    });
  }, [loading, screen, attempt]);

  return slow;
}
